import AppContext from "PFApp/app_context";
import { useGrowl } from "PFApp/use_growl";
import SectionBox from "PFComponents/box/section_box";
import Modal from "PFComponents/modal/modal";
import Radios from "PFComponents/radios/radios";
import TextArea from "PFComponents/text/text_area";
import { Button } from "PFCore/components/button";
import { getProfileName } from "PFCore/helpers/profile";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import WarningIcon from "PFIcons/warning.svg";
import PropTypes from "prop-types";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import { approveRequest, rejectRequest } from "../api";
import css from "./modals.module.scss";
import ReasonForException from "./parts/reason_for_exception";

const ApproveOrRejectModal = ({ close, role, exception, shortlistItem, onSuccess }) => {
  const {
    store: { currentProfile }
  } = useContext(AppContext);
  const { t } = useTranslation("auditPlanner");
  const { getFirstError } = useResponseErrors();
  const { profile } = shortlistItem;
  // eslint-disable-next-line camelcase
  const { request_reason, exceptions, requester } = exception;
  const { availability, compliance } = exceptions;
  const isMultiple = availability && compliance;
  const growl = useGrowl();
  const [shouldApprove, setShouldApprove] = useState(null);
  const [message, setMessage] = useState("");
  const exceptionKeys = Object.keys(exceptions || {});
  const [selectedIndex, setSelectedIndex] = useState(0);
  const exceptionKeySelected = exceptionKeys[selectedIndex];

  const approveOrReject = () => {
    close();
    const apiRequest = shouldApprove ? approveRequest : rejectRequest;

    apiRequest(shortlistItem.id, currentProfile.id, message)
      .then(() => {
        if (onSuccess) {
          onSuccess();
        }
        growl({
          kind: "success",
          message: shouldApprove ? t("modals.requestApproved") : t("modals.requestRejected")
        });
      })
      .catch((response) => {
        const error = getFirstError(response);
        growl({
          kind: "error",
          message: error || t("modals.errorUpdatingException")
        });
      });
  };

  const action = shouldApprove === null ? "approving/rejecting" : shouldApprove ? "approving" : "rejecting";

  return (
    <Modal
      closeOnBackdropClick
      handleClose={close}
      style={{ maxWidth: 900, margin: "0 auto", transform: "translateY(-25px)" }}
    >
      <SectionBox>
        <div className={css.modal}>
          <h3 className={css.title}>
            <WarningIcon className={css.icon} width={50} height={50} />
            {t(`modals.auditPlannerExceptions.${isMultiple ? "multiple" : exceptionKeySelected}`)}
          </h3>
          <hr className={css.separator} />
          <div className={css.table}>
            <div className={css.cell} style={{ width: "25%" }}>
              <div>{t("parts.headers.roleName")}</div>
              <div className={css.bold}>{role.name}</div>
            </div>
            <div className={css.cell} style={{ width: "25%" }}>
              <div>{t("parts.headers.grade")}</div>
              <div className={css.bold}>{role.grade}</div>
            </div>
            <div className={css.cell} style={{ width: "25%" }}>
              <div>{t("parts.headers.resource")}</div>
              <div className={css.bold}>{getProfileName(profile)}</div>
            </div>
            <div className={css.cell} style={{ width: "25%" }}>
              <div>{t("parts.headers.raisedBy")}</div>
              <div className={css.bold}>{getProfileName(requester)}</div>
            </div>
          </div>
          <hr className={css.separator} />
          <div className={css.table}>
            <div className={css.cell} style={{ width: "25%" }}>
              {t("modals.reasonForException")}
            </div>
            <div className={css.bold} style={{ width: "75%" }}>
              <ReasonForException
                profile={profile}
                exception={exception.exceptions}
                onSelectedIndexChange={setSelectedIndex}
                exceptionKeys={exceptionKeys}
                exceptionKeySelected={exceptionKeySelected}
                selectedIndex={selectedIndex}
              />
            </div>
          </div>
          <hr className={css.separator} />
          <div className={css.table}>
            <div className={css.cell} style={{ width: "25%" }}>
              {t("modals.note")}
            </div>
            <div className={css.bold} style={{ width: "75%" }}>
              {request_reason}
            </div>
          </div>
          <hr className={css.separator} />
          <h4 className={css.warning}>{t("modals.whatWouldYouDo")}</h4>
          <div className={css.radiosTextareaWrapper}>
            <div style={{ marginBottom: 15, whiteSpace: "nowrap" }}>
              <Radios
                inline
                items={[
                  { id: "1", text: t("modals.approveRequest") },
                  { id: "0", text: t("modals.rejectRequest") }
                ]}
                value={shouldApprove === null ? shouldApprove : String(Number(shouldApprove))}
                handleChange={(item) => setShouldApprove(!!Number(item.id))}
              />
            </div>

            <TextArea
              className={css.textArea}
              controlledValue
              placeholder={t("modals.reasonForAction", { action: action })}
              value={message}
              onChange={setMessage}
              maxLength={280}
              maxHeight={200}
            />
          </div>
          <div className={css.buttons}>
            <Button text={t("cancel")} kind="tertiary" onClick={close} />
            <Button
              text={t("submit")}
              onClick={approveOrReject}
              disabled={shouldApprove === null || !message}
            />
          </div>
        </div>
      </SectionBox>
    </Modal>
  );
};

ApproveOrRejectModal.propTypes = {
  close: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  role: PropTypes.object.isRequired,
  exception: PropTypes.object.isRequired,
  shortlistItem: PropTypes.shape({
    profile: PropTypes.object.isRequired,
    id: PropTypes.number.isRequired,
    exceptions: PropTypes.arrayOf(PropTypes.object)
  }).isRequired
};

export default ApproveOrRejectModal;
